import { UI, withA, type Col } from "../art/pal.ts";
import { TRAINERS, trainerAt, trainerChallenge, trainerEncounter, trainerLock, type TrainerDef } from "../data/trainers.ts";
import { moveById } from "../data/moves.ts";
import { SPECIES_IDS } from "../data/species.ts";
import {
  MAP,
  MAP_H,
  MAP_W,
  bedAt,
  encountersAt,
  GRASS_QUIET,
  zoneAt,
  ZONES,
  TILE,
  grassEncounterChance,
  spotFacing,
  tileAt,
} from "../data/map.ts";
import { measureText } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import { maxHp, mintMon, type Mon } from "../game/mon.ts";
import { clinicHeal, commitRng, living } from "../game/save.ts";
import { missionText, shinyFromUid, streakReward, STREAK_REWARDS } from "../game/goals.ts";
import { firstStep } from "../game/onboarding.ts";
import { claimStreak } from "../game/goalEvents.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { coin, dialog, glass, hint } from "../ui/kit.ts";
import { ACTOR_AX, ACTOR_AY, actorFrame, TRAINER_OUTFITS, wornOutfit, type Facing } from "../world/actors.ts";
import { clockLabel, DAY_MS, dayLight, gradeWorld, periodOf, Particles, skipToNextPeriod, worldHour, type LightSource } from "../world/fx.ts";
import { clueAt, huntComplete, lairAt } from "../data/secrets.ts";
import { weekOf } from "../game/goals.ts";
import { fbm, hash2, vnoise } from "../world/noise.ts";
import { causticFrame, getWorld, HPX, objSprite, WPX } from "../world/world.ts";
import { followerDrawable, followerSpot, onlineField, plinthDrawable, trailMotes } from "./onlineField.ts";

const RUSTLE_MS = 560;
const WALK_MS = 150;
const RUN_MS = 85;

const DIRS: Record<number, { x: number; y: number }> = {
  0: { x: 0, y: -1 },
  1: { x: 0, y: 1 },
  2: { x: 1, y: 0 },
  3: { x: -1, y: 0 },
};

/** Play time before the save-your-progress reminder, and whether it already showed this page load. */
const KEY_NUDGE_MS = 20 * 60_000;
let keyNudged = false;

export function overworldScene(ctx: Ctx): Scene {
  let moving = 0;
  let moveMs = WALK_MS;
  let fromX = 0;
  let fromY = 0;
  let notice: string | null = null;
  /** A second page queued behind the current notice (a clue after a sign or kettle). */
  let followUp: string | null = null;
  let challenge: TrainerDef | null = null;
  let step = 0 as 0 | 1;
  let anim = 0;
  let grassStreak = 0;
  let rustle = 0;
  let rustleWild: Mon | null = null;
  let arrive = 0;
  let noticeAge = 0;
  let camX = -1;
  let camY = -1;
  let lastZone = "";
  /** The login-streak card shown on the first visit of a day. */
  let streakCard: { streak: number; label: string; age: number } | null = null;
  let dayCheck = 0;

  function maybeStreakCard(): void {
    const d = ctx.state.daily;
    if (!d || d.streakClaimed) return;
    const label = claimStreak(ctx.state);
    if (!label) return;
    ctx.save();
    ctx.audio.levelUp();
    streakCard = { streak: d.streak, label, age: 0 };
  }
  let bannerAge = 1e9;
  let spawnClock = 0;
  /** Cave / neon-grid darkness, eased so the light changes as the scout walks in. */
  let gloom = 0;
  const fx = new Particles();
  const grassHits = new Map<string, number>();
  const online = onlineField(ctx);

  const face = () => DIRS[ctx.state.dir] ?? DIRS[1]!;
  /** This scout's lead showcase Crown, if the live world says they hold one. */
  const selfCrown = () => {
    const e = ctx.net.live ? ctx.net.econ : null;
    const oid = e?.showcase[0];
    return oid ? e.crowns.find((c) => c.oid === oid) ?? null : null;
  };
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location?.search ?? "" : "");
  const timeOverride = params.get("time");
  const period = () => periodOf(worldHour(ctx.state, timeOverride));
  /** Trainer checks need the week: encores (the post-game) rotate every Monday. */
  const trainerState = () => ({ ...ctx.state, week: weekOf(ctx.today()) });

  function tryStep(dx: number, dy: number): void {
    if (moving > 0) return;
    if (dx === 1) ctx.state.dir = 2;
    if (dx === -1) ctx.state.dir = 3;
    if (dy === 1) ctx.state.dir = 1;
    if (dy === -1) ctx.state.dir = 0;
    const nx = ctx.state.x + dx;
    const ny = ctx.state.y + dy;
    const tile = tileAt(nx, ny);
    if (tile.solid || trainerAt(nx, ny)) {
      ctx.audio.back();
      return;
    }
    fromX = ctx.state.x;
    fromY = ctx.state.y;
    ctx.state.x = nx;
    ctx.state.y = ny;
    ctx.goal("step");
    const zoneTo = zoneAt(nx, ny);
    if (zoneTo !== zoneAt(fromX, fromY)) ctx.goal("zone", 1, { zone: zoneTo });
    const running = ctx.input.down.has("Shift");
    moveMs = running ? RUN_MS : WALK_MS;
    moving = ctx.reduced ? 1 : moveMs;
    step = step === 0 ? 1 : 0;
    ctx.audio.step();
    if (!ctx.reduced) {
      const wx = nx * TILE + 8, wy = ny * TILE + 15;
      if (tile.tall) {
        grassHits.set(`${nx},${ny}`, ctx.now);
        for (let i = 0; i < 4; i++) fx.spawn({ kind: "leaf", x: wx + (Math.random() - 0.5) * 10, y: wy - 8 - Math.random() * 6, vx: (Math.random() - 0.5) * 30, vy: -20 - Math.random() * 20, max: 600 + Math.random() * 300 });
      } else if (running) {
        fx.spawn({ kind: "dust", x: fromX * TILE + 8, y: fromY * TILE + 14, vx: -dx * 12, vy: -4, max: 320 });
      }
    }
  }

  function finishStep(): void {
    moving = 0;
    const tile = tileAt(ctx.state.x, ctx.state.y);
    if (!tile.tall) return;
    if (living(ctx.state).length === 0) return;
    if (ctx.grassQuiet > 0) {
      ctx.grassQuiet -= 1;
      commitRng(ctx.state, ctx.rng);
      return;
    }
    grassStreak += 1;
    const p = grassEncounterChance(grassStreak);
    if (!ctx.rng.chance(p)) {
      commitRng(ctx.state, ctx.rng);
      return;
    }
    grassStreak = 0;
    const now = period();
    const here = bedAt(ctx.state.x, ctx.state.y);
    // A legend whose trail is complete waits in its lair bed at its hour, until it is caught.
    const lair = lairAt(here.zone, here.bed, now, ctx.state.clues, ctx.state.caught);
    const pool = encountersAt(ctx.state.x, ctx.state.y, ctx.state.defeatedTrainers.includes("vell"), now)
      .flatMap((row) => Array<typeof row>(row.w).fill(row));
    if (!pool.length && !lair) return;
    let wild: Mon;
    if (lair) wild = mintMon(ctx.rng, lair.legend, lair.level);
    else {
      const row = ctx.rng.pick(pool);
      const lv = ctx.rng.int(row.lo, row.hi);
      wild = mintMon(ctx.rng, row.id, lv, "wild");
    }
    // One wild creature in 256 is shiny, read off its uid: no extra dice, so seeds replay the same.
    if (shinyFromUid(wild.uid)) wild.shiny = true;
    commitRng(ctx.state, ctx.rng);
    ctx.audio.encounter();
    if (ctx.reduced) {
      ctx.push("battle", { wild });
      return;
    }
    rustle = RUSTLE_MS;
    rustleWild = wild;
  }

  function interact(): void {
    const f = face();
    const peer = ctx.net.live ? online.peerAt(ctx.state.x + f.x, ctx.state.y + f.y) : null;
    if (peer) {
      online.openMenu(peer);
      return;
    }
    const trainer = trainerAt(ctx.state.x + f.x, ctx.state.y + f.y) ?? trainerAt(ctx.state.x, ctx.state.y);
    if (trainer) {
      ctx.audio.confirm();
      const locked = trainerLock(trainer, trainerState());
      if (locked) notice = `${trainer.name}: ${locked}`;
      else if (!living(ctx.state).length) notice = "The crew needs Cope Clinic before a duel. Nobody duels on empty.";
      else {
        challenge = trainerEncounter(trainer, trainerState());
        notice = `${trainer.name}: ${trainerChallenge(trainer, trainerState())}`;
      }
      return;
    }
    const spot = spotFacing(ctx.state.x, ctx.state.y, f.x, f.y);
    if (!spot) {
      notice = `Just mud and opinions. ${SPECIES_IDS.length} jokes wait. Walk anyway.`;
      return;
    }
    ctx.audio.confirm();
    const clue = spot.kind === "rest" || spot.kind === "sign" ? clueAt(spot.title, period(), ctx.state.clues) : null;
    if (clue) {
      ctx.state.clues.push(clue.clue.id);
      const n = clue.hunt.clues.filter((c) => ctx.state.clues.includes(c.id)).length;
      followUp = `Clue ${n}/3: ${clue.clue.text}${huntComplete(clue.hunt, ctx.state.clues)
        ? ` Trail complete: something waits in ${clue.hunt.lair} at ${clue.hunt.when}.` : ""}`;
      ctx.save();
    }
    if (spot.kind === "rest") {
      const skip = skipToNextPeriod(worldHour(ctx.state, timeOverride));
      ctx.state.clockShift = (ctx.state.clockShift + skip.ms) % DAY_MS;
      clinicHeal(ctx.state);
      ctx.goal("rest");
      ctx.grassQuiet = GRASS_QUIET;
      grassStreak = 0;
      ctx.audio.heal();
      ctx.save();
      notice = `${spot.title}: ${spot.body} Crew HP and PP restored. You doze off; it is ${skip.period} now.`;
      if (!ctx.reduced) {
        for (let i = 0; i < 10; i++) fx.spawn({ kind: "spark", x: ctx.state.x * TILE + 8 + (Math.random() - 0.5) * 14, y: ctx.state.y * TILE + 4, vx: (Math.random() - 0.5) * 30, vy: -40 - Math.random() * 30, max: 700, color: [120, 255, 170, 255] });
      }
    } else if (spot.kind === "heal") ctx.push("clinic");
    else if (spot.kind === "shop") ctx.push("shop");
    else if (spot.kind === "pc") ctx.push("pc");
    else if (spot.kind === "market") ctx.push("market");
    else if (spot.kind === "raid") ctx.push("raid");
    else if (spot.kind === "crowns") ctx.push("crownHouse");
    else if (spot.kind === "hall") ctx.push("crownHouse", { tab: "HALL" });
    else notice = `${spot.title}: ${ctx.state.defeatedTrainers.includes("vell") ? spot.veteranBody ?? spot.body : spot.body}`;
  }

  /** Ambient life: pollen & butterflies by day, fireflies by night, kettle steam, chimney smoke. */
  function ambient(night: number): void {
    if (ctx.reduced) return;
    const world = getWorld();
    spawnClock += ctx.dt;
    const biome = world.biome[Math.max(0, Math.min(MAP_H - 1, ctx.state.y)) * MAP_W + Math.max(0, Math.min(MAP_W - 1, ctx.state.x))]!;
    while (spawnClock > 90) {
      spawnClock -= 90;
      const x = camX + Math.random() * SCREEN_W, y = camY + Math.random() * SCREEN_H;
      if (biome === 1) {
        for (let k = 0; k < 2; k++) fx.spawn({ kind: "snow", x: camX + Math.random() * SCREEN_W, y: camY - 4 + Math.random() * 20, vx: 0, vy: 14 + Math.random() * 16, max: 9000 });
        continue;
      }
      if (biome === 3) {
        if (Math.random() < 0.6) fx.spawn({ kind: "ember", x, y: camY + SCREEN_H * (0.5 + Math.random() * 0.5), vx: 0, vy: -10 - Math.random() * 14, max: 3200 });
        continue;
      }
      if (biome === 5) {
        if (Math.random() < 0.5) fx.spawn({ kind: "glint", x, y, vx: 0, vy: -2, max: 1600, color: Math.random() < 0.5 ? [200, 250, 255, 255] : [230, 200, 255, 255] });
        continue;
      }
      if (biome === 4) {
        if (Math.random() < 0.3) fx.spawn({ kind: "firefly", x, y, vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8, max: 4000 });
        if (Math.random() < 0.15) fx.spawn({ kind: "steam", x, y, vx: 1, vy: -3, max: 3000 });
        continue;
      }
      if (biome === 2) {
        if (Math.random() < 0.25) fx.spawn({ kind: "dust", x: camX - 4, y, vx: 30 + Math.random() * 20, vy: -1, max: 2400 });
        continue;
      }
      if (biome === 6) {
        if (Math.random() < 0.45) fx.spawn({ kind: "glint", x, y, vx: (Math.random() - 0.5) * 4, vy: -3, max: 2600, color: Math.random() < 0.6 ? [90, 232, 208, 255] : [180, 255, 90, 255] });
        continue;
      }
      if (biome === 7) {
        if (night < 0.5 && Math.random() < 0.2) fx.spawn({ kind: "pollen", x, y, vx: 14 + Math.random() * 8, vy: -1, max: 2600 });
        if (Math.random() < 0.15) fx.spawn({ kind: "glint", x, y, vx: 0, vy: 0, max: 900, color: [255, 255, 255, 255] });
        continue;
      }
      if (biome === 8) {
        if (Math.random() < 0.35) fx.spawn({ kind: "steam", x: camX - 6, y, vx: 18 + Math.random() * 10, vy: 0, max: 5000 });
        if (Math.random() < 0.25) fx.spawn({ kind: "glint", x, y, vx: 0, vy: -1, max: 1400, color: [255, 240, 160, 255] });
        continue;
      }
      if (biome === 9) {
        if (Math.random() < 0.5) fx.spawn({ kind: "glint", x, y, vx: 0, vy: -6, max: 1200, color: Math.random() < 0.5 ? [255, 90, 216, 255] : [62, 240, 224, 255] });
        continue;
      }
      if (biome === 10) {
        // Falling leaves drifting across the fields.
        if (Math.random() < 0.4) {
          const leaf: [number, number, number, number][] = [[255, 138, 60, 255], [255, 208, 96, 255], [200, 64, 44, 255]];
          fx.spawn({ kind: "leaf", x, y: camY - 4, vx: 8 + Math.random() * 10, vy: 10 + Math.random() * 8, max: 7000, color: leaf[Math.floor(Math.random() * 3)]! });
        }
        continue;
      }
      if (biome === 11) {
        // Graveyard: slow wisps and low fog.
        if (Math.random() < 0.3) fx.spawn({ kind: "glint", x, y, vx: (Math.random() - 0.5) * 3, vy: -2, max: 3200, color: Math.random() < 0.6 ? [154, 232, 200, 255] : [200, 180, 255, 255] });
        if (Math.random() < 0.2) fx.spawn({ kind: "steam", x: camX - 6, y, vx: 10 + Math.random() * 6, vy: 0, max: 5200 });
        continue;
      }
      if (biome === 12) {
        if (Math.random() < 0.3) fx.spawn({ kind: "firefly", x, y, vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8, max: 4000 });
        if (night < 0.5 && Math.random() < 0.04) fx.spawn({ kind: "butterfly", x, y, vx: 10, vy: 0, max: 6000, color: Math.random() < 0.5 ? [80, 200, 255, 255] : [255, 90, 138, 255] });
        continue;
      }
      if (biome === 13) {
        // Blossom petals on the breeze.
        if (Math.random() < 0.55) fx.spawn({ kind: "petal", x: x - 20, y: camY - 4 + Math.random() * SCREEN_H * 0.5, vx: 12 + Math.random() * 10, vy: 6 + Math.random() * 6, max: 6500, color: Math.random() < 0.5 ? [255, 180, 205, 255] : [255, 225, 236, 255] });
        continue;
      }
      if (biome === 14) {
        if (Math.random() < 0.3) fx.spawn({ kind: "dust", x, y, vx: 4 + Math.random() * 4, vy: -1, max: 2600 });
        if (Math.random() < 0.12) fx.spawn({ kind: "glint", x, y, vx: 0, vy: -1, max: 1400, color: [255, 224, 140, 255] });
        continue;
      }
      if (night < 0.5) {
        if (Math.random() < 0.35) fx.spawn({ kind: "pollen", x, y, vx: 6 + Math.random() * 6, vy: -2 + Math.random() * 3, max: 3000 });
        if (Math.random() < 0.025) fx.spawn({ kind: "butterfly", x, y, vx: 10, vy: 0, max: 6000, color: Math.random() < 0.5 ? [255, 230, 120, 255] : [255, 160, 200, 255] });
      } else if (Math.random() < 0.3) {
        fx.spawn({ kind: "firefly", x, y, vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8, max: 4000 });
      }
      for (const o of world.objects) {
        if (o.kind !== "kettle") continue;
        if (Math.abs(o.x - camX - SCREEN_W / 2) > 260 || Math.abs(o.y - camY - SCREEN_H / 2) > 160) continue;
        if (Math.random() < 0.3) fx.spawn({ kind: "steam", x: o.x + (Math.random() - 0.5) * 3, y: o.y - 12, vx: 0, vy: -10 - Math.random() * 6, max: 1200 });
      }
      for (const c of world.chimneys) {
        if (Math.random() < 0.25) fx.spawn({ kind: "smoke", x: c.x + (Math.random() - 0.5) * 3, y: c.y, vx: 2, vy: -9 - Math.random() * 4, max: 2600 });
      }
    }
  }

  function trainerFacing(t: TrainerDef): Facing {
    const dx = ctx.state.x - t.x, dy = ctx.state.y - t.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) return 1;
    if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 2 : 3;
    return dy < 0 ? 0 : 1;
  }

  return {
    id: "overworld",
    native: true,
    debug: () => ({ notice, followUp, challenge: challenge?.id ?? null, moving, rustle, streakCard: !!streakCard }),
    enter(payload?: ScenePayload) {
      moving = 0;
      notice = payload?.notice ?? null;
      followUp = payload?.followUp ?? null;
      challenge = null;
      rustle = 0;
      rustleWild = null;
      arrive = ctx.shot ? 1000 : 0;
      noticeAge = ctx.shot ? 1000 : 0;
      camX = camY = -1;
      ctx.net.join(() => ctx.state);
      const here = zoneAt(ctx.state.x, ctx.state.y);
      if (!ctx.state.stats.zoneVisits[here]) ctx.goal("zone", 1, { zone: here });
      ctx.rollDaily();
      maybeStreakCard();
    },
    exit() {},
    update() {
      anim += ctx.dt;
      arrive += ctx.dt;
      dayCheck += ctx.dt;
      if (dayCheck > 60_000) {
        // Midnight while playing: a new day, new missions and the next streak step.
        dayCheck = 0;
        if (ctx.rollDaily()) maybeStreakCard();
      }
      // A guest with real play and no way back yet: one reminder per visit, until the key is saved.
      if (!keyNudged && !notice && !streakCard && ctx.net.live && ctx.state.playMs >= KEY_NUDGE_MS && !ctx.net.keySaved()) {
        keyNudged = true;
        notice = "Your scout only lives on this device for now. Save it in 10 seconds: Pause > ONLINE > A > Save my progress.";
      }
      if (streakCard) {
        streakCard.age += ctx.dt;
        if (streakCard.age > 400 && ctx.input.consumeAny("Enter", "Escape")) {
          ctx.audio.confirm();
          streakCard = null;
        }
        return;
      }
      const zoneNow = zoneAt(ctx.state.x, ctx.state.y);
      ctx.audio.music(zoneNow === "stubtown" || zoneNow === "frontierPost" || zoneNow === "neonHarbor" || zoneNow === "bridgeport" ? "town" : "route");
      bannerAge += ctx.dt;
      if (notice) noticeAge += ctx.dt;
      else noticeAge = ctx.shot ? 1000 : 0;
      if (!ctx.reduced) fx.update(ctx.dt);
      if (rustle > 0) {
        rustle -= ctx.dt;
        if (ctx.input.consumeAny("Enter", "Escape") || rustle <= 0) {
          const wild = rustleWild;
          rustle = 0;
          rustleWild = null;
          if (wild) ctx.push("battle", { wild });
        }
        return;
      }
      if (notice) {
        if (ctx.input.consume("Escape")) {
          notice = followUp;
          followUp = null;
          noticeAge = 0;
          challenge = null;
        } else if (ctx.input.consume("Enter")) {
          const trainer = challenge;
          notice = followUp;
          followUp = null;
          noticeAge = 0;
          challenge = null;
          if (trainer) ctx.push("battle", { trainer: trainer.id });
        }
        return;
      }
      ctx.net.move(ctx.state.x, ctx.state.y, ctx.state.dir, moveMs === RUN_MS);
      if (online.update(moving <= 0)) return;
      if (moving > 0) {
        moving = ctx.reduced ? 0 : moving - ctx.dt;
        const f = face();
        const canUse = trainerAt(ctx.state.x + f.x, ctx.state.y + f.y)
          ?? trainerAt(ctx.state.x, ctx.state.y)
          ?? spotFacing(ctx.state.x, ctx.state.y, f.x, f.y);
        if (canUse && ctx.input.consume("Enter")) {
          moving = 0;
          interact();
          return;
        }
        if (moving <= 0) finishStep();
        return;
      }
      if (ctx.input.consume("Enter")) {
        interact();
        return;
      }
      if (ctx.input.consume("Escape") || ctx.input.consume("Tab")) {
        ctx.audio.confirm();
        ctx.push("pause");
        return;
      }
      const tap = ctx.input.dirPressed();
      if (tap) tryStep(tap.x, tap.y);
      else {
        const hold = ctx.input.heldDir(ctx.now);
        if (hold) tryStep(hold.x, hold.y);
      }
    },
    draw() {
      const g = ctx.g;
      const world = getWorld();
      const reduced = ctx.reduced;
      const hour = worldHour(ctx.state, timeOverride);
      const dl = dayLight(hour);
      const skyNight = dl.night;
      // Caves are always dark and the Neon Grid always dusky; ease between them as the scout walks.
      const here = world.biome[Math.max(0, Math.min(MAP_H - 1, ctx.state.y)) * MAP_W + Math.max(0, Math.min(MAP_W - 1, ctx.state.x))]!;
      const gloomTarget = here === 6 ? 0.82 : here === 9 ? 0.55 : here === 11 ? 0.5 : 0;
      gloom = reduced || ctx.shot ? gloomTarget : gloom + (gloomTarget - gloom) * (1 - Math.exp(-ctx.dt / 400));
      if (gloom > dl.night) {
        const k = gloom - dl.night;
        dl.amb = dl.amb.map((c, i) => c * (1 - k * 0.62) + [0.22, 0.2, 0.38][i]! * k * 0.62) as [number, number, number];
        dl.night = gloom;
        dl.golden *= 1 - k;
      }
      const night = dl.night;

      // ---- Camera -------------------------------------------------------------------------
      const t = moving > 0 && !reduced ? 1 - moving / moveMs : 1;
      const ease = t;
      const px = (fromX + (ctx.state.x - fromX) * (moving > 0 ? ease : 1)) * TILE;
      const py = (fromY + (ctx.state.y - fromY) * (moving > 0 ? ease : 1)) * TILE;
      const f = face();
      const lookX = moving > 0 ? f.x * 10 : 0, lookY = moving > 0 ? f.y * 6 : 0;
      const tx = Math.max(0, Math.min(WPX - SCREEN_W, px + 8 - SCREEN_W / 2 + lookX));
      const ty = Math.max(0, Math.min(HPX - SCREEN_H, py + 4 - SCREEN_H / 2 + lookY));
      if (camX < 0 || reduced || ctx.shot) { camX = tx; camY = ty; }
      else {
        const k = 1 - Math.exp(-ctx.dt / 110);
        camX += (tx - camX) * k;
        camY += (ty - camY) * k;
      }
      let cx = Math.round(camX), cy = Math.round(camY);
      if (rustle > 0 && !reduced) {
        const shake = Math.max(0, (rustle - RUSTLE_MS * 0.45) / (RUSTLE_MS * 0.55));
        cx += Math.round(Math.sin(anim / 25) * 2 * shake);
      }

      // ---- Ground + water -------------------------------------------------------------------
      g.copyRegion(world.ground, cx, cy, SCREEN_W, SCREEN_H, 0, 0);
      drawWater(g, world, cx, cy, reduced ? 0 : anim, reduced);

      // ---- Collect sortables -----------------------------------------------------------------
      type Drawable = { y: number; x: number; top: number; img: ImageData; shadow?: boolean; tie: number };
      const items: Drawable[] = [];
      const x0 = cx - 80, x1 = cx + SCREEN_W + 80, y0 = cy - 16, y1 = cy + SCREEN_H + 90;
      const standKey = `${ctx.state.x},${ctx.state.y}`;
      for (const o of world.objects) {
        if (o.x < x0 || o.x > x1 || o.y < y0 || o.y > y1) continue;
        const hitAt = o.kind === "tall" ? grassHits.get(`${o.tx},${o.ty}`) : undefined;
        const ambientRustle = o.kind === "tall" && !reduced && hash2(o.tx, o.ty, Math.floor(anim / 900)) > 0.985 && (anim % 900) < 360;
        const rustling = o.kind === "tall" && (ambientRustle || (hitAt !== undefined && ctx.now - hitAt < 380) || (rustle > 0 && `${o.tx},${o.ty}` === standKey));
        const spr = objSprite(o, anim, night > 0.5, reduced, rustling);
        items.push({ y: o.y, x: o.x - spr.ax, top: o.y - spr.ay, img: spr.img, tie: o.kind === "tall" ? 2 : 0 });
      }
      for (const tr of Object.values(TRAINERS)) {
        const wx = tr.x * TILE + 8, wy = tr.y * TILE + 15;
        if (wx < x0 || wx > x1 || wy < y0 || wy > y1) continue;
        const bob = reduced ? 0 : Math.floor((anim + tr.x * 97) / 520) % 2;
        const img = actorFrame(`tr:${tr.id}`, TRAINER_OUTFITS[tr.id], trainerFacing(tr), 0);
        items.push({ y: wy, x: wx - ACTOR_AX, top: wy - ACTOR_AY - bob, img, shadow: true, tie: 1 });
      }
      const walkFrame = moving > 0 && !reduced ? (t < 0.5 ? (step ? 1 : 3) : (step ? 2 : 0)) : 0;
      const worn = wornOutfit(ctx.state.cosmetics);
      const pimg = actorFrame(worn.key, worn.outfit, ctx.state.dir as Facing, walkFrame);
      const pwx = Math.round(px) + 8, pwy = Math.round(py) + 15;
      const hop = moving > 0 && !reduced && moveMs === RUN_MS ? Math.round(Math.sin(t * Math.PI)) : 0;
      items.push({ y: pwy, x: pwx - ACTOR_AX, top: pwy - ACTOR_AY - hop, img: pimg, shadow: true, tie: 1 });
      for (const d of online.drawables(anim, reduced)) if (d.x > x0 && d.x < x1 && d.y > y0 && d.y < y1) items.push(d);
      const lead = selfCrown();
      if (lead && ctx.net.econ?.cosmetics.includes("plinth")) items.push(plinthDrawable(pwx, pwy, ctx.state.dir));
      if (lead) items.push(followerDrawable(lead.species, lead.shiny, pwx, pwy, ctx.state.dir, anim, reduced));

      items.sort((a, b) => a.y - b.y || a.tie - b.tie);
      for (const it of items) {
        const sx = Math.round(it.x - cx), sy = Math.round(it.top - cy);
        if (it.shadow) g.ellipse(it.x - cx + ACTOR_AX + 1, it.y - cy, 6, 2.2, [26, 22, 51, 90], 0.6);
        g.blit(it.img, sx, sy);
      }

      // ---- FX + lighting ---------------------------------------------------------------------
      ambient(night);
      fx.draw(g, cx, cy, night);
      const lights: LightSource[] = [];
      for (const L of world.lights) {
        const lx = L.x - cx, ly = L.y - cy;
        if (lx < -L.r || ly < -L.r || lx > SCREEN_W + L.r || ly > SCREEN_H + L.r) continue;
        const k = L.nightOnly ? night : 0.25 + night * 0.9;
        if (k <= 0.02) continue;
        const fl = L.flicker && !reduced ? 0.9 + 0.1 * Math.sin(anim / 90 + L.x) : 1;
        lights.push({ x: lx, y: ly, r: L.r * fl, color: L.color, k: k * 1.25 });
      }
      if (night > 0.3) lights.push({ x: pwx - cx, y: pwy - cy - 10, r: 44, color: [255, 220, 170, 255], k: 0.5 * night });
      gradeWorld(g, dl, lights, cx, cy, anim, reduced);
      if (night > 0.05) {
        for (const L of world.lights) {
          if (!L.nightOnly || L.r < 50) continue;
          const lx = L.x - cx, ly = L.y - cy;
          if (lx < -20 || ly < -20 || lx > SCREEN_W + 20 || ly > SCREEN_H + 20) continue;
          g.glow(lx, ly + 4, 12, [255, 210, 130, 255], 0.55 * night);
        }
      }

      // ---- HUD ------------------------------------------------------------------------------
      const zoneName = ZONES[zoneAt(ctx.state.x, ctx.state.y)].name;
      if (zoneName !== lastZone) {
        if (lastZone) bannerAge = 0;
        lastZone = zoneName;
      }
      const hudIn = reduced || ctx.shot ? 1 : Math.min(1, arrive / 300);
      const hudY = Math.round(-20 * (1 - hudIn));
      // Money pill.
      const money = `${ctx.state.money}`;
      // The coin icon names the currency (coins); no symbol after the amount.
      const mw = measureText(money) + 28;
      glass(g, 6, 6 + hudY, mw, 17, { r: 8, alpha: 210 });
      coin(g, 12, 10 + hudY, reduced ? 0 : anim / 400);
      g.textShadow(25, 11 + hudY, money, UI.goldHi, UI.ink);
      // Zone + clock pill.
      const zoneLabel = zoneName.toUpperCase();
      const clock = clockLabel(hour);
      const zw = measureText(zoneLabel) + measureText(clock) + 30;
      glass(g, SCREEN_W - zw - 6, 6 + hudY, zw, 17, { r: 8, alpha: 210 });
      g.textShadow(SCREEN_W - zw + 3, 11 + hudY, zoneLabel, UI.text, UI.ink);
      const sunX = SCREEN_W - measureText(clock) - 22;
      if (skyNight > 0.5) { g.disc(sunX + 3.5, 14.5 + hudY, 3.5, [220, 230, 255, 255]); g.disc(sunX + 5, 13.5 + hudY, 3, withA(UI.bg1, 255)); }
      else g.disc(sunX + 3.5, 14.5 + hudY, 3.5, dl.golden > 0.3 ? [255, 160, 90, 255] : UI.gold);
      g.textShadow(SCREEN_W - measureText(clock) - 12, 11 + hudY, clock, UI.textDim, UI.ink);

      // Zone banner when crossing into a new area.
      if (bannerAge < 2600 && !reduced) {
        const inT = Math.min(1, bannerAge / 260), outT = Math.max(0, (bannerAge - 2100) / 500);
        const a = Math.min(inT, 1 - outT);
        const bw = measureText(zoneName, 2) + 40;
        const bx = Math.round((SCREEN_W - bw) / 2), by = 40 + Math.round((1 - inT) * -10);
        g.fill(bx, by, bw, 26, [10, 12, 24, 170 * a]);
        g.fill(bx, by, bw, 1, withA(UI.gold, 255 * a));
        g.fill(bx, by + 25, bw, 1, withA(UI.gold, 255 * a));
        g.textShadow(bx + 20, by + 6, zoneName, withA(UI.text, 255 * a), [20, 16, 40, 200 * a], 2);
      }

      // Trainer "!" balloons (hidden behind an open duel card).
      for (const rival of challenge ? [] : Object.values(TRAINERS)) {
        if (trainerLock(rival, trainerState())) continue;
        const rx = rival.x * TILE - cx + 8, ry = rival.y * TILE - cy - 20;
        if (rx < -10 || ry < -10 || rx > SCREEN_W + 10 || ry > SCREEN_H) continue;
        const b = reduced ? 0 : Math.round(Math.abs(Math.sin(anim / 300 + rival.x)) * 2);
        const done = ctx.state.defeatedTrainers.includes(rival.id);
        g.rrect(rx - 4, ry - 6 - b, 9, 10, 3, UI.ink);
        g.rrect(rx - 3, ry - 5 - b, 7, 8, 2, done ? UI.teal : UI.gold);
        g.text(rx - 1, ry - 4 - b, done ? "·" : "!", UI.ink);
        g.p(rx, ry + 4 - b, UI.ink);
      }

      if (selfCrown() && ctx.net.econ?.cosmetics.includes("trail")) {
        const fs = followerSpot(pwx, pwy, ctx.state.dir);
        trailMotes(g, Math.round(fs.x - cx), Math.round(fs.y - cy), anim, reduced);
      }
      online.drawOverlay(g, cx, cy, { x: pwx, y: pwy }, hudY, anim);

      // Interaction hint.
      if (!notice && !rustle && !moving) {
        const rival = trainerAt(ctx.state.x + f.x, ctx.state.y + f.y);
        const spot = spotFacing(ctx.state.x, ctx.state.y, f.x, f.y);
        const peer = ctx.net.live ? online.peerAt(ctx.state.x + f.x, ctx.state.y + f.y) : null;
        const label = peer ? `A  ${peer.name}` : rival ? `A  ${rival.name}` : spot ? `A  ${spot.kind === "rest" ? "Rest - HP / PP" : spot.title}` : "";
        if (label) {
          const text = label.slice(3);
          const w = measureText(text) + 26;
          const hx = Math.round((SCREEN_W - w) / 2), hy = SCREEN_H - 26;
          glass(g, hx, hy, w, 17, { r: 8, alpha: 220 });
          hint(g, hx + 4, hy + 3, "A", text, "a");
        }
      }

      // First steps for a new scout, until the opening loop has been played once.
      const coach = ctx.shot || notice || rustle > 0 ? null : firstStep(ctx.state);
      if (coach) {
        const w = measureText(coach) + 52;
        glass(g, 6, 44 + hudY, w, 15, { r: 7, alpha: 215, border: withA(UI.gold, 200) });
        g.textShadow(12, 48 + hudY, "NEXT", UI.goldHi, UI.ink);
        g.text(42, 48 + hudY, coach, UI.text);
      }

      // Notices / duel card / rustle.
      if (rustle > 0) {
        drawEncounterWipe(g, 1 - rustle / RUSTLE_MS, pwx - cx, pwy - cy - 12, reduced);
        g.textShadow(12, SCREEN_H - 16, "The reeds shiver.", UI.text, UI.ink);
      } else if (notice) {
        if (challenge) drawDuelCard(ctx, challenge, noticeAge);
        else {
          const split = notice.indexOf(": ");
          const named = split > 0 && split < 26;
          dialog(g, named ? notice.slice(split + 2) : notice, {
            name: named ? notice.slice(0, split) : undefined, more: true, now: anim, reduced, age: noticeAge,
          });
        }
      }

      if (streakCard) drawStreakCard(ctx, streakCard.streak, streakCard.label, streakCard.age);

      if (ctx.state.cosmetics.frame) frameTint(g);
      if (!reduced && !ctx.shot && arrive < 260) g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, Math.round(255 * (1 - arrive / 260))]);
    },
  };
}

/** First visit of the day: the streak step just paid and today's missions. */
function drawStreakCard(ctx: Ctx, streak: number, label: string, age: number): void {
  const g = ctx.g;
  const reduced = ctx.reduced;
  const k = reduced ? 1 : Math.min(1, age / 260);
  g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, Math.round(160 * k)]);
  const y = 30 + Math.round((1 - k) * 24);
  glass(g, 52, y, 280, 156, { r: 10, border: UI.gold });
  g.gradV(53, y + 1, 278, 20, withA(UI.gold, 200), withA(UI.bg2, 80), 9);
  g.textShadow(64, y + 7, streak > 1 ? `DAY ${streak} STREAK!` : "WELCOME BACK!", UI.white, UI.ink);
  const pos = (streak - 1) % STREAK_REWARDS.length;
  for (let i = 0; i < STREAK_REWARDS.length; i++) {
    const cx = 72 + i * 36, cy = y + 40;
    const pulse = i === pos && !reduced ? Math.sin(ctx.now / 180) * 1.5 : 0;
    g.disc(cx, cy, (i === pos ? 10 : 8) + pulse, i < pos ? UI.green : i === pos ? UI.gold : UI.bg3);
    g.text(cx - 2, cy - 3, `${i + 1}`, i <= pos ? UI.paperInk : UI.textMute);
  }
  g.textShadow(64, y + 58, `Today: ${label}`, UI.goldHi, UI.ink);
  g.text(64, y + 70, `Tomorrow: ${streakReward(streak + 1).label}. Keep the streak alive!`, UI.textDim);
  g.text(64, y + 88, "TODAY'S MISSIONS", UI.textMute);
  (ctx.state.daily?.missions ?? []).forEach((m, i) => {
    g.text(70, y + 100 + i * 11, `${m.live ? "◆" : "•"} ${missionText(m)}${m.live ? " (online)" : ""}`, m.live ? UI.pink : UI.text);
  });
  hint(g, 64, y + 142, "A", "let's go  ·  Pause → GOALS to claim", "a");
}

function frameTint(g: import("../engine/gfx.ts").Gfx): void {
  const c: Col = [255, 200, 90, 255];
  for (let i = 0; i < 3; i++) {
    const a = 150 - i * 45;
    g.rrectLine(i, i, SCREEN_W - i * 2, SCREEN_H - i * 2, 8, withA(c, a));
  }
}

function drawWater(g: import("../engine/gfx.ts").Gfx, world: ReturnType<typeof getWorld>, cx: number, cy: number, t: number, reduced: boolean): void {
  const d = g.img.data;
  const wm = world.water, depth = world.depth, shore = world.shore, bank = world.bank;
  const caust = causticFrame(reduced ? 0 : Math.floor(t / 140));
  const phase = t / 260;
  for (let y = 0; y < SCREEN_H; y++) {
    const wy = y + cy;
    if (wy < 0 || wy >= HPX) continue;
    for (let x = 0; x < SCREEN_W; x++) {
      const wx = x + cx;
      const i = wy * WPX + wx;
      if (!wm[i]) continue;
      const o = (y * SCREEN_W + x) * 4;
      const dep = Math.max(0, Math.min(1, (depth[i]! - 0.5) * 1.6));
      const biome = world.biome[(wy >> 4) * MAP_W + (wx >> 4)]!;
      if (biome >= 3 || biome === 1) {
        const c = biomeWater(biome, wx, wy, dep, bank[i]!, shore[i]!, t, reduced, caust);
        d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2];
        continue;
      }
      // Base: shallow turquoise → mid lagoon blue.
      let r = 96 - dep * 52, gg = 206 - dep * 92, b = 236 - dep * 52;
      const bk = bank[i]!;
      if (bk) {
        // Bank face: wet earth wall under the north shore, then a dark contact line.
        const k = bk === 1 ? 0 : bk === 2 ? 1 : 2;
        const bankC = [[128, 88, 58], [90, 62, 48], [44, 52, 88]][k]!;
        r = bankC[0]!; gg = bankC[1]!; b = bankC[2]!;
      } else {
        const c = caust[((wy + Math.floor(Math.sin((wx + t / 40) * 0.05) * 3)) & 63) * 64 + ((wx + Math.floor(t / 90)) & 63)]!;
        const ck = (c / 255) * (0.5 - dep * 0.25);
        r += (230 - r) * ck; gg += (250 - gg) * ck; b += (255 - b) * ck;
        const sd = shore[i]!;
        if (sd <= 3) {
          const wave = 0.5 + 0.5 * Math.sin(phase - sd * 1.3 + (wx * 0.15 + wy * 0.1));
          const foam = sd === 1 ? 0.75 : sd === 2 ? 0.45 * wave : 0.25 * wave * wave;
          r += (240 - r) * foam; gg += (252 - gg) * foam; b += (255 - b) * foam;
        }
        if (!reduced && sd > 3 && hash2(wx >> 1, wy, Math.floor(t / 420)) > 0.9985) { r = 255; gg = 255; b = 255; }
      }
      d[o] = r; d[o + 1] = gg; d[o + 2] = b;
    }
  }
  void MAP; void MAP_W; void MAP_H;
}

/** Ice, lava, bog water and crystal pools share the water mask but not its look. */
function biomeWater(biome: number, wx: number, wy: number, dep: number, bk: number, sd: number, t: number, reduced: boolean, caust: Uint8Array): [number, number, number] {
  if (bk) {
    const banks: Record<number, [number, number, number][]> = {
      1: [[200, 220, 236], [150, 176, 200], [90, 110, 150]],
      3: [[70, 58, 70], [46, 38, 48], [120, 40, 30]],
      4: [[88, 76, 52], [62, 54, 40], [40, 52, 44]],
      5: [[176, 160, 220], [120, 104, 176], [60, 60, 120]],
      6: [[70, 80, 104], [48, 56, 76], [24, 30, 44]],
      7: [[236, 212, 170], [210, 180, 136], [120, 190, 200]],
      8: [[255, 255, 255], [220, 230, 250], [150, 176, 230]],
      9: [[62, 240, 224], [40, 36, 80], [20, 16, 44]],
      10: [[176, 130, 88], [128, 90, 62], [60, 70, 110]],
      11: [[96, 90, 118], [66, 60, 86], [30, 28, 48]],
      12: [[120, 96, 64], [84, 66, 46], [30, 60, 50]],
      13: [[220, 212, 196], [180, 170, 156], [70, 110, 120]],
      14: [[220, 200, 150], [170, 150, 110], [70, 110, 110]],
    };
    return banks[biome]![bk === 1 ? 0 : bk === 2 ? 1 : 2]!;
  }
  const cv = caust[(wy & 63) * 64 + (wx & 63)]! / 255;
  if (biome === 1) {
    // Ice: pale, still, with frozen crack lines and a white rim.
    const crack = caust[((wy * 2) & 63) * 64 + ((wx * 2 + 17) & 63)]! / 255;
    let r = 214 - dep * 60, g = 238 - dep * 40, b = 252 - dep * 16;
    if (crack > 0.55) { r -= 40; g -= 30; b -= 18; }
    if (sd <= 2) { r = 246; g = 252; b = 255; }
    if (hash2(wx >> 1, wy >> 1, 5) > 0.997) { r = 255; g = 255; b = 255; }
    return [r, g, b];
  }
  if (biome === 3) {
    // Lava: bright molten veins under a slow-moving dark crust.
    const slow = causticFrame(reduced ? 0 : Math.floor(t / 420));
    const v = slow[((wy + Math.floor(t / 300)) & 63) * 64 + ((wx - Math.floor(t / 500)) & 63)]! / 255;
    const crust = fbm(wx / 14 + t / 9000, wy / 14, 2, 3);
    let r = 255, g = 110 + v * 120, b = 30 + v * 60;
    if (crust > 0.58 && v < 0.35) { r = 90; g = 38; b = 40; }
    else if (crust > 0.52 && v < 0.2) { r = 170; g = 60; b = 40; }
    if (sd <= 1) { r = 60; g = 40; b = 46; }
    return [r, g, b];
  }
  if (biome === 4) {
    // Bog: murky green-brown, dim ripples, scum at the edge and a few lily pads.
    let r = 70 - dep * 26, g = 96 - dep * 36, b = 66 - dep * 22;
    r += cv * 18; g += cv * 22; b += cv * 10;
    if (sd <= 2) { r = 120; g = 136; b = 84; }
    const cx = (wx >> 3) * 8 + 4, cy = (wy >> 3) * 8 + 4;
    if (hash2(wx >> 3, wy >> 3, 11) < 0.07 && (wx - cx) ** 2 + (wy - cy) ** 2 * 1.6 < 9) { r = 110; g = 170; b = 80; if (wx === cx + 1 && wy === cy) { r = 255; g = 170; b = 200; } }
    return [r, g, b];
  }
  if (biome === 6) {
    // Underground pool: near-black water, faint teal caustics, drifting glow specks.
    let r = 18 + cv * 30, g = 30 + cv * 60, b = 44 + cv * 60;
    if (sd <= 1) { r = 60; g = 90; b = 110; }
    if (hash2(wx >> 1, wy >> 1, Math.floor(t / 700)) > 0.994) { r = 90; g = 232; b = 208; }
    return [r, g, b];
  }
  if (biome === 7) {
    // Tropical lagoon: bright turquoise, coral heads under the shallows, white surf.
    let r = 70 - dep * 40, g = 214 - dep * 70, b = 222 - dep * 30;
    r += (240 - r) * cv * 0.35; g += (255 - g) * cv * 0.3; b += (255 - b) * cv * 0.3;
    const cell = hash2(wx >> 4, wy >> 4, 21);
    if (dep < 0.5 && cell < 0.35 && vnoise(wx / 5, wy / 5, 22) > 0.62) {
      const cc = [[255, 140, 130], [255, 190, 110], [220, 140, 240]][Math.floor(cell * 9) % 3]!;
      r = r * 0.45 + cc[0]! * 0.55; g = g * 0.45 + cc[1]! * 0.55; b = b * 0.45 + cc[2]! * 0.55;
    }
    if (sd <= 1) { r = 250; g = 255; b = 255; }
    else if (sd === 2 && !reduced && Math.sin(t / 300 + (wx + wy) * 0.2) > 0.4) { r = 220; g = 250; b = 255; }
    return [r, g, b];
  }
  if (biome === 8) {
    // Open sky through a gap in the cloud floor: blue deepening with depth, clouds drifting far below.
    const drift = reduced ? 0 : t / 60;
    const far = fbm((wx + drift) / 40, wy / 26, 3, 23);
    let r = 150 - dep * 90, g = 190 - dep * 80, b = 250 - dep * 20;
    if (far > 0.6) { const k = Math.min(1, (far - 0.6) * 4); r += (245 - r) * k; g += (248 - g) * k; b += (255 - b) * k; }
    if (sd <= 1) { r = 255; g = 255; b = 255; }
    return [r, g, b];
  }
  if (biome === 9) {
    // Data stream: dark channel with scrolling columns of lit bits.
    let r = 16, g = 12, b = 40;
    const col = wx >> 2;
    const speed = 30 + hash2(col, 0, 24) * 50;
    const yy = Math.floor((wy + (reduced ? 0 : t / speed * 4)) / 4);
    const bit = hash2(col, yy, 25);
    if ((wx & 3) !== 3 && (wy & 3) !== 3 && bit > 0.55) {
      const hot = bit > 0.9;
      r = hot ? 200 : 40; g = hot ? 255 : 200 * (bit - 0.4); b = hot ? 240 : 180 * (bit - 0.4) + 40;
    }
    if (sd <= 1) { r = 62; g = 240; b = 224; }
    return [r, g, b];
  }
  if (biome === 10) {
    // Farm pond: calm blue with a few floating leaves.
    let r = 90 - dep * 40, g = 170 - dep * 70, b = 220 - dep * 40;
    r += (230 - r) * cv * 0.3; g += (245 - g) * cv * 0.3; b += (255 - b) * cv * 0.3;
    if (hash2(wx >> 2, wy >> 2, 31) < 0.04) { const k = hash2(wx >> 2, wy >> 2, 32); r = k < 0.5 ? 240 : 200; g = k < 0.5 ? 140 : 70; b = 50; }
    if (sd <= 1) { r = 236; g = 244; b = 250; }
    return [r, g, b];
  }
  if (biome === 11) {
    // Haunted pool: violet-black, green glow rising from below.
    let r = 40 + cv * 20, g = 34 + cv * 50, b = 60 + cv * 40;
    if (hash2(wx >> 1, wy >> 1, Math.floor(t / 900)) > 0.993) { r = 154; g = 232; b = 200; }
    if (sd <= 1) { r = 110; g = 100; b = 140; }
    return [r, g, b];
  }
  if (biome === 12) {
    // Jungle river: green-teal water sliding downstream.
    const flow = caust[((wy - Math.floor(reduced ? 0 : t / 50)) & 63) * 64 + (wx & 63)]! / 255;
    let r = 60 - dep * 20, g = 150 - dep * 50, b = 120 - dep * 30;
    r += (200 - r) * flow * 0.35; g += (250 - g) * flow * 0.35; b += (230 - b) * flow * 0.35;
    if (sd <= 1) { r = 220; g = 245; b = 230; }
    return [r, g, b];
  }
  if (biome === 13) {
    // Koi pond: clear teal, orange-and-white koi gliding, petals afloat.
    let r = 110 - dep * 50, g = 200 - dep * 60, b = 210 - dep * 30;
    r += (240 - r) * cv * 0.3; g += (255 - g) * cv * 0.3; b += (255 - b) * cv * 0.3;
    for (let k = 0; k < 2; k++) {
      const phase = (reduced ? 0 : t / 2600) + k * 3.1;
      const kx = 8 + Math.cos(phase) * 6, ky = 8 + Math.sin(phase * 1.3) * 4;
      const lx = (((wx >> 0) % 16) + 16) % 16, ly = (((wy >> 0) % 16) + 16) % 16;
      if (hash2(wx >> 4, wy >> 4, 40 + k) < 0.5 && (lx - kx) ** 2 + ((ly - ky) * 2) ** 2 < 5) {
        const white = (lx + ly) % 3 === 0;
        r = white ? 255 : 250; g = white ? 250 : 130; b = white ? 245 : 60;
      }
    }
    if (hash2(wx >> 1, wy >> 1, 44) > 0.996) { r = 255; g = 190; b = 210; }
    if (sd <= 1) { r = 240; g = 250; b = 250; }
    return [r, g, b];
  }
  if (biome === 14) {
    // Sacred pool: deep turquoise with gold coins glinting on the bottom.
    let r = 60 - dep * 30, g = 190 - dep * 60, b = 190 - dep * 40;
    r += (220 - r) * cv * 0.4; g += (255 - g) * cv * 0.4; b += (250 - b) * cv * 0.4;
    if (hash2(wx >> 1, wy >> 1, 45) > 0.985) { r = 255; g = 214; b = 90; }
    if (sd <= 1) { r = 240; g = 232; b = 200; }
    return [r, g, b];
  }
  // Crystal pool: glowing cyan with white caustics and sparkles.
  let r = 110 - dep * 40, g = 236 - dep * 70, b = 232 - dep * 40;
  r += (250 - r) * cv * 0.6; g += (255 - g) * cv * 0.5; b += (255 - b) * cv * 0.5;
  if (sd <= 1) { r = 220; g = 210; b = 255; }
  if (!reduced && hash2(wx, wy, Math.floor(t / 300)) > 0.996) { r = 255; g = 255; b = 255; }
  return [r, g, b];
}

/** Encounter wipe: a "!" pop, then diagonal bars closing in. */
function drawEncounterWipe(g: import("../engine/gfx.ts").Gfx, t: number, px: number, py: number, reduced: boolean): void {
  if (reduced) return;
  if (t < 0.45) {
    const pop = Math.min(1, t / 0.12);
    const s = pop < 1 ? pop * 1.2 : 1;
    const h = Math.round(12 * s);
    g.rrect(px - 5, py - 18 - h + 12, 11, h, 3, UI.ink);
    g.rrect(px - 4, py - 17 - h + 12, 9, h - 2, 3, UI.pink);
    if (pop >= 1) g.text(px - 1, py - 15, "!", UI.white);
    return;
  }
  const k = (t - 0.45) / 0.55;
  const bars = 12;
  for (let i = 0; i < bars; i++) {
    const delay = (i % 2 ? 0.1 : 0) + i * 0.02;
    const p = Math.max(0, Math.min(1, (k - delay) / 0.6));
    const e = p * p * (3 - 2 * p);
    const hBar = Math.ceil(SCREEN_H / bars) + 1;
    const y = i * (SCREEN_H / bars);
    const w = Math.round(SCREEN_W * e);
    if (i % 2) g.fill(0, Math.floor(y), w, hBar, [10, 8, 22, 255]);
    else g.fill(SCREEN_W - w, Math.floor(y), w, hBar, [10, 8, 22, 255]);
    if (w > 0 && w < SCREEN_W) {
      const ex = i % 2 ? w : SCREEN_W - w;
      g.fill(ex - 1, Math.floor(y), 2, hBar, UI.gold);
    }
  }
}

function drawDuelCard(ctx: Ctx, challenge: TrainerDef, age: number): void {
  const g = ctx.g;
  const reduced = ctx.reduced;
  const pop = reduced ? 1 : Math.min(1, age / 180);
  const x = 20, w = SCREEN_W - 40, h = 150;
  const y = Math.round(SCREEN_H - h - 12 + (1 - pop) * 16);
  glass(g, x, y, w, h, { r: 8 });
  // Header band.
  g.gradV(x + 1, y + 1, w - 2, 20, UI.goldHi, UI.gold, 7);
  g.hline(x + 1, y + 21, w - 2, UI.goldDk);
  g.text(x + 12, y + 7, `DUEL / ${challenge.name}`, UI.paperInk);
  const levels = challenge.team.map((m) => m.level);
  const lo = Math.min(...levels), hi = Math.max(...levels);
  const tag = `${challenge.team.length} crew  Lv${lo === hi ? lo : `${lo}-${hi}`}`;
  g.text(x + w - measureText(tag) - 12, y + 7, tag, UI.paperInk);
  // Portrait.
  const pimg = actorFrame(`tr:${challenge.id}`, TRAINER_OUTFITS[challenge.id], 1, 0);
  g.rrect(x + 10, y + 30, 44, 56, 6, UI.bg0);
  g.gradV(x + 11, y + 31, 42, 54, [60, 70, 130, 255], [26, 30, 60, 255], 5);
  g.ellipse(x + 32, y + 80, 12, 3, [0, 0, 0, 90]);
  g.blit(pimg, x + 16, y + 30, { scale: 2 });
  // Offer text.
  g.para(x + 64, y + 30, trainerChallenge(TRAINERS[challenge.id], { ...ctx.state, week: weekOf(ctx.today()) }), w - 76, UI.text, 11, 5);
  // Readiness.
  const rested = ctx.state.party.filter((m) => m.hp === maxHp(m) && m.moves.length > 0
    && m.moves.every((slot) => slot.pp >= moveById(slot.id).pp)).length;
  const lead = ctx.state.party.find((m) => m.hp > 0)!;
  const stock = (n: number) => (n > 999 ? "999+" : String(n));
  g.rrect(x + 10, y + 94, w - 20, 28, 5, [10, 12, 26, 200]);
  g.text(x + 18, y + 99, `Rested ${rested}/${ctx.state.party.length} HP+PP / Lead Lv${lead.level}`, rested === ctx.state.party.length ? UI.green : UI.yellow);
  g.text(x + 18, y + 110, `Full Cope ${stock(ctx.state.bag.fullcope)}  Sip ${stock(ctx.state.bag.sip)}  Tea ${stock(ctx.state.bag.cleartea)}`, UI.textDim);
  const hx = x + w - 150;
  hint(g, hx, y + h - 20, "A", "Duel", "a");
  hint(g, hx + 64, y + h - 20, "B", "Leave", "b");
}

